import { api } from './api';

// =============================================================================
// Tipos
// =============================================================================

export type DreRegime = 'cash' | 'accrual' | 'hybrid';

export type DreGroup =
  | 'REVENUE'
  | 'REVENUE_DEDUCTION'
  | 'VARIABLE_COST'
  | 'FIXED_EXPENSE'
  | 'INVESTMENT'
  | 'NON_OP_INCOME'
  | 'NON_OP_EXPENSE';

export type DreNature = 'OPERATIONAL' | 'INVESTING' | 'FINANCING';

export interface ChartOfAccount {
  id: string;
  unit_id: string | null;
  code: string;
  name: string;
  description: string | null;
  parent_id: string | null;
  level: number;
  is_synthetic: boolean;
  dre_group: DreGroup;
  nature: DreNature;
  sign: 1 | -1;
  sort_order: number;
  is_active: boolean;
  is_system: boolean;
  auto_source: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface DreComputeRow {
  bucket: string;
  account_id: string;
  account_code: string;
  account_name: string;
  account_level: number;
  parent_id: string | null;
  dre_group: DreGroup;
  sign: 1 | -1;
  sort_order: number;
  realized: number;
  expected: number;
  value_for_regime: number;
  av_pct: number | null;
  ah_pct: number | null;
}

export type DreSummaryRowKind =
  | 'REVENUE_TOTAL'
  | 'REVENUE_DEDUCTION'
  | 'NET_REVENUE'
  | 'VARIABLE_COST'
  | 'CONTRIBUTION_MARGIN'
  | 'FIXED_EXPENSE'
  | 'PROFIT_BEFORE_INV'
  | 'INVESTMENT'
  | 'OPERATING_EXPENSE'
  | 'OPERATING_PROFIT'
  | 'NON_OPERATIONAL'
  | 'NET_RESULT'
  | 'BREAKEVEN_NO_INV'
  | 'BREAKEVEN_WITH_INV'
  | 'CASH_RECONCILE';

export interface DreSummaryRow {
  bucket: string;
  row_kind: DreSummaryRowKind;
  row_label: string;
  row_order: number;
  realized: number;
  expected: number;
  value_for_regime: number;
  av_pct: number | null;
  ah_pct: number | null;
}

export interface DreDrilldownRow {
  source: string;
  source_id: string;
  unit_id: string | null;
  competence_date: string | null;
  cash_date: string | null;
  amount: number;
  status: string;
  description: string | null;
  category: string | null;
  patient_id: string | null;
  patient_name?: string | null;
  professional_id: string | null;
  professional_name?: string | null;
  appointment_id: string | null;
  payment_method: string | null;
  metadata: Record<string, unknown>;
  account_id?: string;
  account_code?: string;
  account_name?: string;
  dre_group?: DreGroup;
}

export interface DreSettings {
  id: string;
  unit_id: string | null;
  default_revenue_recognition: 'cash' | 'accrual' | 'hybrid';
  revenue_competence_source: 'procedure_date' | 'nfse_date' | 'due_date' | 'paid_date';
  expense_competence_source: 'due_date' | 'paid_date' | 'invoice_date';
  shared_expense_behavior: 'skip' | 'each' | 'prorate';
  break_even_capacity_metric: 'revenue' | 'appointments' | 'tickets';
  auto_classify_legacy: boolean;
  cash_reconciliation_source: 'physical_diff' | 'bank_minus_book';
  snapshot_retention_days: number;
  metadata: Record<string, unknown>;
}

export interface DreSnapshot {
  id: string;
  unit_id: string | null;
  unit_ids: string[];
  consolidated: boolean;
  period_start: string;
  period_end: string;
  period_label: string;
  period_granularity: 'monthly' | 'quarterly' | 'yearly';
  regime: DreRegime;
  payload: { summary: DreSummaryRow[]; details: DreComputeRow[] };
  total_revenue: number | null;
  total_variable_cost: number | null;
  total_contribution_margin: number | null;
  total_fixed_expense: number | null;
  total_investment: number | null;
  total_operating_profit: number | null;
  total_non_operational: number | null;
  total_net_result: number | null;
  break_even_before_investment: number | null;
  break_even_with_investment: number | null;
  cash_reconciliation: number | null;
  is_locked: boolean;
  notes: string | null;
  created_at: string;
}

export interface AutoClassifyResult {
  source_table: string;
  total_rows: number;
  classified: number;
  unclassified: number;
}

// =============================================================================
// Helpers internos
// =============================================================================

const BASE = '/financial-module/dre';
const yyyymmdd = (d: Date) => d.toISOString().slice(0, 10);

function toStr(d: Date | string): string {
  return typeof d === 'string' ? d : yyyymmdd(d);
}

/** Limites do mês do bucket YYYY-MM-01 (calendário local). Evita periodStart > periodEnd com new Date(iso) em fusos ≠ UTC. */
export function dreMonthBoundsFromBucket(bucket: string): { periodStart: string; periodEnd: string } {
  const m = /^(\d{4})-(\d{2})-\d{2}/.exec(String(bucket ?? '').trim());
  if (!m) return { periodStart: '', periodEnd: '' };
  const y = Number(m[1]);
  const mo = Number(m[2]);
  if (!y || !mo || mo < 1 || mo > 12) return { periodStart: '', periodEnd: '' };
  const lastDay = new Date(y, mo, 0).getDate();
  const ym = `${y}-${String(mo).padStart(2, '0')}`;
  return {
    periodStart: `${ym}-01`,
    periodEnd: `${ym}-${String(lastDay).padStart(2, '0')}`,
  };
}

// =============================================================================
// Cliente
// =============================================================================

export const dreService = {
  // ---- Compute ---------------------------------------------------------------
  async compute(params: {
    periodStart: Date | string;
    periodEnd: Date | string;
    unitIds?: string[] | null;
    regime?: DreRegime;
  }): Promise<DreComputeRow[]> {
    const { data } = await api.post(`${BASE}/compute`, {
      period_start: toStr(params.periodStart),
      period_end: toStr(params.periodEnd),
      unit_ids: params.unitIds?.length ? params.unitIds : null,
      regime: params.regime || 'cash',
    });
    return data.data || [];
  },

  /** Detalhe + resumo em uma requisição (backend calcula só uma vez; cache opcional em memória). */
  async computeBundle(params: {
    periodStart: Date | string;
    periodEnd: Date | string;
    unitIds?: string[] | null;
    regime?: DreRegime;
    nocache?: boolean;
  }): Promise<{ summary: DreSummaryRow[]; detail: DreComputeRow[]; cacheHit?: boolean }> {
    const { data } = await api.post(
      `${BASE}/compute-bundle`,
      {
        period_start: toStr(params.periodStart),
        period_end: toStr(params.periodEnd),
        unit_ids: params.unitIds?.length ? params.unitIds : null,
        regime: params.regime || 'cash',
        nocache: params.nocache === true,
      },
      { timeout: 5 * 60 * 1000 },
    );
    const pack = data.data || {};
    return {
      summary: (pack.summary || []) as DreSummaryRow[],
      detail: (pack.detail || []) as DreComputeRow[],
      cacheHit: data.meta?.cache_hit === true,
    };
  },

  async computeSummary(params: {
    periodStart: Date | string;
    periodEnd: Date | string;
    unitIds?: string[] | null;
    regime?: DreRegime;
  }): Promise<DreSummaryRow[]> {
    const { data } = await api.post(`${BASE}/compute-summary`, {
      period_start: toStr(params.periodStart),
      period_end: toStr(params.periodEnd),
      unit_ids: params.unitIds?.length ? params.unitIds : null,
      regime: params.regime || 'cash',
    });
    return data.data || [];
  },

  // ---- Drilldown -------------------------------------------------------------
  async drilldownAccount(params: {
    accountId?: string;
    accountIds?: string[];
    periodStart: Date | string;
    periodEnd: Date | string;
    unitIds?: string[] | null;
    regime?: DreRegime;
  }): Promise<DreDrilldownRow[]> {
    const ids =
      params.accountIds && params.accountIds.length > 0
        ? params.accountIds
        : params.accountId
          ? [params.accountId]
          : [];
    if (!ids.length) return [];
    const { data } = await api.post(`${BASE}/drilldown-account`, {
      account_ids: ids,
      period_start: toStr(params.periodStart),
      period_end: toStr(params.periodEnd),
      unit_ids: params.unitIds?.length ? params.unitIds : null,
      regime: params.regime || 'cash',
    });
    return data.data || [];
  },

  async drilldownSummary(params: {
    rowKind: DreSummaryRowKind;
    periodStart: Date | string;
    periodEnd: Date | string;
    unitIds?: string[] | null;
    regime?: DreRegime;
  }): Promise<DreDrilldownRow[]> {
    const { data } = await api.post(`${BASE}/drilldown-summary`, {
      row_kind: params.rowKind,
      period_start: toStr(params.periodStart),
      period_end: toStr(params.periodEnd),
      unit_ids: params.unitIds?.length ? params.unitIds : null,
      regime: params.regime || 'cash',
    });
    return data.data || [];
  },

  // ---- Chart of Accounts CRUD ------------------------------------------------
  async listChartOfAccounts(unitId?: string | null): Promise<ChartOfAccount[]> {
    const params: Record<string, string> = {};
    if (unitId) params.unit_id = unitId;
    const { data } = await api.get(`${BASE}/chart-of-accounts`, { params });
    return data.data || [];
  },

  async createChartOfAccount(input: Partial<ChartOfAccount>): Promise<ChartOfAccount> {
    const { data } = await api.post(`${BASE}/chart-of-accounts`, input);
    return data.data;
  },

  async updateChartOfAccount(id: string, input: Partial<ChartOfAccount>): Promise<ChartOfAccount> {
    const { data } = await api.put(`${BASE}/chart-of-accounts/${id}`, input);
    return data.data;
  },

  async deleteChartOfAccount(id: string): Promise<void> {
    await api.delete(`${BASE}/chart-of-accounts/${id}`);
  },

  // ---- Settings --------------------------------------------------------------
  async getSettings(unitId?: string | null): Promise<DreSettings | null> {
    const params: Record<string, string> = {};
    if (unitId) params.unit_id = unitId;
    const { data } = await api.get(`${BASE}/settings`, { params });
    return data.data || null;
  },

  async updateSettings(unitId: string | null, input: Partial<DreSettings>): Promise<DreSettings> {
    const { data } = await api.put(`${BASE}/settings`, { unit_id: unitId, ...input });
    return data.data;
  },

  // ---- Auto-classify ---------------------------------------------------------
  async autoClassify(dryRun = false): Promise<AutoClassifyResult[]> {
    const { data } = await api.post(`${BASE}/auto-classify`, { dry_run: dryRun });
    return data.data || [];
  },

  // ---- Snapshots -------------------------------------------------------------
  async listSnapshots(unitId?: string | null): Promise<DreSnapshot[]> {
    const params: Record<string, string> = {};
    if (unitId) params.unit_id = unitId;
    const { data } = await api.get(`${BASE}/snapshots`, { params });
    return data.data || [];
  },

  async createSnapshot(input: {
    unit_id: string | null;
    unit_ids: string[];
    consolidated: boolean;
    period_start: string;
    period_end: string;
    period_label: string;
    period_granularity?: 'monthly' | 'quarterly' | 'yearly';
    regime: DreRegime;
    payload: { summary: DreSummaryRow[]; details: DreComputeRow[] };
    notes?: string;
  }): Promise<DreSnapshot> {
    // Calcula totais resumidos do payload para guardar nos campos de índice
    const totals = input.payload.summary.reduce<Record<string, number>>((acc, r) => {
      acc[r.row_kind] = (acc[r.row_kind] || 0) + (Number(r.value_for_regime) || 0);
      return acc;
    }, {});

    const row = {
      ...input,
      period_granularity: input.period_granularity || 'monthly',
      total_revenue: totals.REVENUE_TOTAL ?? null,
      total_variable_cost: totals.VARIABLE_COST ?? null,
      total_contribution_margin: totals.CONTRIBUTION_MARGIN ?? null,
      total_fixed_expense: totals.FIXED_EXPENSE ?? null,
      total_investment: totals.INVESTMENT ?? null,
      total_operating_profit: totals.OPERATING_PROFIT ?? null,
      total_non_operational: totals.NON_OPERATIONAL ?? null,
      total_net_result: totals.NET_RESULT ?? null,
      break_even_before_investment: totals.BREAKEVEN_NO_INV ?? null,
      break_even_with_investment: totals.BREAKEVEN_WITH_INV ?? null,
      cash_reconciliation: totals.CASH_RECONCILE ?? null,
    };

    const { data } = await api.post(`${BASE}/snapshots`, row);
    return data.data;
  },

  async deleteSnapshot(id: string): Promise<void> {
    await api.delete(`${BASE}/snapshots/${id}`);
  },
};

// =============================================================================
// Helpers utilitários exportados
// =============================================================================

export function formatBRL(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '–';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}

export function formatPct(value: number | null | undefined, decimals = 1): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '–';
  return `${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}%`;
}

export const MONTHS_PT_SHORT = [
  'JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN',
  'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ',
];

export const MONTHS_PT_LONG = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

export function bucketToMonthIndex(bucket: string): number {
  const m = parseInt(bucket.slice(5, 7), 10);
  return Math.max(0, Math.min(11, m - 1));
}

export function bucketToYear(bucket: string): number {
  return parseInt(bucket.slice(0, 4), 10);
}

export const DRE_GROUP_COLORS: Record<DreGroup, string> = {
  REVENUE: '#0EA5E9',
  REVENUE_DEDUCTION: '#F87171',
  VARIABLE_COST: '#F59E0B',
  FIXED_EXPENSE: '#A855F7',
  INVESTMENT: '#6366F1',
  NON_OP_INCOME: '#22C55E',
  NON_OP_EXPENSE: '#DC2626',
};

export const DRE_GROUP_LABELS: Record<DreGroup, string> = {
  REVENUE: 'Receita',
  REVENUE_DEDUCTION: 'Dedução',
  VARIABLE_COST: 'Custo Variável',
  FIXED_EXPENSE: 'Despesa Fixa',
  INVESTMENT: 'Investimento',
  NON_OP_INCOME: 'Não Op. (Receita)',
  NON_OP_EXPENSE: 'Não Op. (Despesa)',
};
